import { DateTime } from 'luxon'
import { useNavigate, useParams } from 'react-router-dom'
import { useStyles } from './BuyersOverview.styles'
import {
  upcomingRenewalsOverviewInfoText,
  standardLabraDateFormat,
  searchDropDownOptions,
  useAccountId,
  useScreenSize,
  LoadingTypes,
  isLoading,
  numberFormat,
  resolveBuyerLogoUrl,
} from '@labrav/common'
import { OverviewSectionWrapper } from '../FlyoutOverview/OverviewSectionWrapper/OverviewSectionWrapper'
import { Grid } from '@mui/material'
import {
  faTriangleExclamation,
  faRepeat,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from '../../store/hooks'
import { getFlyoutOverview } from '../../modules/flyoutOverview/action'
import { UpcomingRenewals } from '../FlyoutOverview/UpcomingRenewals/UpcomingRenewals'
import { LoadingStateComponent } from '@labrav/common'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { subscriptionsRangeStatus } from '../../utils/subscriptionsFilterMapping'
import renwalOff from '../../common/images/renewalOff.svg'
import {
  CardsContainer,
  LabraToolTipV2,
  OfferCard,
  StatusChipBg,
  StatusChipText,
  StatusChipV3,
  VerticalDivider,
  SearchBoxComponent,
} from '@labrav/react-components'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../common/components/DetailRow/DetailRow'
import {
  clearBuyersOverView,
  getBuyersOverview,
} from '../../modules/buyers/action'
import { BuyersData } from '../../modules/buyers/types'
import { useTimeZone } from '../../common/utils/hooks/useTimeZone'
import { getCloudImg } from '../../common/utils/getCloudImg'
import { SearchResultsCount } from '@labrav/common'
import { getProducts } from '../../modules/productsListing/action'
import { Product } from '../../modules/productsListing/types'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { useSyncCloudMarketplace } from '@labrav/common'

const EMPTY_PRODUCT_ROWS: Product[] = []

export const Buyers = () => {
  useSyncFlyoutRouteParams()
  useSyncCloudMarketplace()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { accountId: accountIdFromParams } = useParams<{
    accountId?: string
  }>()
  const dispatch = useDispatch()
  const { timeZone } = useTimeZone()
  const { screenWidth } = useScreenSize()
  const isBelowMediumScreen = screenWidth <= 1230
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId = accountIdFromParams || accountIdFromHook || ''
  const isBuyersPageLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.BUYERS_OVERVIEW)
  )
  const isOverviewPageLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FLYOUT_OVERVIEW)
  )
  const isProductsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const buyersData = useSelector<BuyersData>(state => state.BuyersOverView)

  const buyers = useMemo(() => {
    if (!buyersData?.buyers) return []

    const sortedBuyers = [...buyersData?.buyers].sort((a, b) => {
      const aRenewal = a?.details?.renewal
      const bRenewal = b?.details?.renewal

      if (!aRenewal && bRenewal) return 1
      if (aRenewal && !bRenewal) return -1
      if (!aRenewal && !bRenewal) return 0

      const aDate = DateTime.fromISO(aRenewal, { zone: timeZone })
      const bDate = DateTime.fromISO(bRenewal, { zone: timeZone })
      const currentDate = DateTime.now().setZone(timeZone)

      const aDays = Math.floor(aDate.diff(currentDate, 'days').days)
      const bDays = Math.floor(bDate.diff(currentDate, 'days').days)

      return aDays - bDays
    })

    return sortedBuyers
  }, [buyersData, timeZone])

  const productRows = useSelector<Product[]>(state => {
    const rows = state.productsListing['AWS']?.products?.rows
    return Array.isArray(rows) ? rows : EMPTY_PRODUCT_ROWS
  })
  const goToSubscriptions = (status: subscriptionsRangeStatus) => {
    const subscriptionsPath = accountId
      ? `/flyout/aws/${accountId}/subscriptions`
      : '/flyout/subscriptions'
    navigate(subscriptionsPath, {
      state: {
        status: status,
      },
    })
  }

  const renderRenewalValue = (
    renewal: string,
    autoRenew: boolean,
    activeContracts: number
  ) => {
    if (!renewal) {
      return activeContracts > 0 ? (
        <div>--</div>
      ) : (
        <div className={classes.contractRed}>Contract ended</div>
      )
    }

    const renewalDate = DateTime.fromISO(renewal, {
      zone: timeZone,
    })
    const currentDate = DateTime.now().setZone(timeZone)
    const daysUntilRenewal = Math.floor(
      renewalDate.diff(currentDate, 'days').days
    )

    let icon = (
      <img
        src={renwalOff}
        data-testid={'renewal-icon'}
        className={classes.renewalOffIcon}
      />
    )

    if (autoRenew) {
      icon = <FontAwesomeIcon icon={faRepeat} className={classes.renewalIcon} />
    }

    if (daysUntilRenewal < 0) {
      return activeContracts === 0 ? (
        <div className={classes.contractRed}>
          <LabraToolTipV2
            tooltipPlacement="top"
            label={
              <>
                <div>{renewalDate?.toFormat(standardLabraDateFormat)}</div>{' '}
                <div>Note: Contract ended on</div>
              </>
            }
          >
            {renewalDate?.toFormat(standardLabraDateFormat)}
          </LabraToolTipV2>
        </div>
      ) : (
        <>--</>
      )
    } else if (daysUntilRenewal <= 120 && daysUntilRenewal > 0) {
      return (
        <LabraToolTipV2
          tooltipPlacement="top"
          label={
            <>
              <div>{renewalDate.toFormat(standardLabraDateFormat)}</div>{' '}
              <div>Note: Auto-renewal is {autoRenew ? 'on' : 'off'}</div>
            </>
          }
        >
          <div
            className={
              daysUntilRenewal <= 90
                ? classes.contractRed
                : classes.contractOrange
            }
          >
            {icon}
            {daysUntilRenewal} {daysUntilRenewal === 1 ? 'day' : 'days'}
          </div>
        </LabraToolTipV2>
      )
    }

    const formattedDate = renewalDate.toFormat(standardLabraDateFormat)

    return (
      <LabraToolTipV2
        tooltipPlacement="top"
        label={`Note: Auto-renewal is ${autoRenew ? 'on' : 'off'}`}
      >
        {' '}
        <div>
          {icon}
          {formattedDate}
        </div>
      </LabraToolTipV2>
    )
  }

  const getRenewalTitle = (renewal: string) => {
    if (!renewal) {
      return 'Renewal due in'
    }

    const renewalDate = DateTime.fromISO(renewal, {
      zone: timeZone,
    })
    const currentDate = DateTime.now().setZone(timeZone)
    const daysUntilRenewal = renewalDate.diff(currentDate, 'days').days

    if (daysUntilRenewal <= 120 && daysUntilRenewal > 0) {
      return 'Renewal due in'
    } else if (daysUntilRenewal < 0) {
      return 'Renewal due in'
    } else {
      return 'Renewal due on'
    }
  }

  const goToBuyersDetail = (buyerId: string) => {
    const buyersPath = accountId
      ? `/flyout/aws/${accountId}/buyers/${buyerId}`
      : `/flyout/buyers/${buyerId}`
    navigate(buyersPath)
  }

  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [selectedOption, setSelectedOption] = useState(
    searchDropDownOptions.buyersOptions[0]
  )

  useEffect(() => {
    if (accountId) {
      if (!searchNow) {
        const fetchData = async () => {
          await Promise.all([
            dispatch(getProducts(accountId, 'AWS')),
            dispatch(getFlyoutOverview({ partnerId: accountId })),
            dispatch(clearBuyersOverView()),
            dispatch(getBuyersOverview({ partnerId: accountId })),
          ])
        }
        fetchData()
      }
    }
  }, [accountId, searchNow, dispatch])

  const handleSearchClick = async (inputValue: string) => {
    if (inputValue.trim() !== '') {
      setSearchNow(true)
      setShowCrossMark(true)
      dispatch(clearBuyersOverView())
      dispatch(getBuyersOverview({ partnerId: accountId, inputValue }))
    }
  }

  return (
    <>
      {isProductsLoading &&
      !searchNow &&
      (isBuyersPageLoading || isOverviewPageLoading) ? (
        <>
          <LoadingStateComponent
            heading="Loading your buyers overview page"
            description="It may take a few moments, please wait..."
          />
        </>
      ) : (
        <>
          {productRows &&
          !productRows?.length &&
          !isProductsLoading &&
          (!isBuyersPageLoading || !isOverviewPageLoading) ? (
            <ListingCreationPage />
          ) : (
            <div className={classes.root} data-testid={'buyers-overview-page'}>
              <Grid container className={classes.content} spacing={4}>
                <Grid
                  className={classes.container}
                  size={{
                    xs: 12,
                    sm: 12,
                    md: isBelowMediumScreen ? 12 : 7,
                    lg: 7,
                  }}
                >
                  <div className={classes.serachWrapper}>
                    <SearchBoxComponent
                      options={searchDropDownOptions.buyersOptions}
                      handleSearchClick={handleSearchClick}
                      loading={isBuyersPageLoading}
                      searchNow={searchNow}
                      setSearchNow={setSearchNow}
                      showCrossMark={showCrossMark}
                      setShowCrossMark={setShowCrossMark}
                      selectedOption={selectedOption}
                      setSelectedOption={setSelectedOption}
                    />
                  </div>
                  {searchNow && (
                    <SearchResultsCount
                      totalCount={buyers.length}
                      loading={isBuyersPageLoading}
                    />
                  )}
                  {buyers?.map((buyer, index) => (
                    <CardsContainer
                      key={buyer.buyerInfo.companyName || index}
                      hoverEffect={true}
                      handleClick={() => {
                        dispatch(clearBuyersOverView())
                        goToBuyersDetail(buyer?.labraBuyerDetailsId)
                      }}
                    >
                      <OfferCard
                        logo={resolveBuyerLogoUrl(buyer?.buyerInfo?.logoUrl)}
                        name={
                          buyer?.buyerInfo?.companyName !== 'Unknown buyer' ? (
                            buyer?.buyerInfo?.companyName
                          ) : (
                            <LabraToolTipV2
                              tooltipPlacement="top"
                              label="Buyer remains unknown as they haven't completed registration. If you have their details, please fill out the form.."
                            >
                              Unknown buyer
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                className={classes.infoIcon}
                              />
                            </LabraToolTipV2>
                          )
                        }
                        onArrowClick={() => {
                          dispatch(clearBuyersOverView())
                          goToBuyersDetail(buyer?.labraBuyerDetailsId)
                        }}
                        isLastCard={true}
                        customiseForBuyer={true}
                        rightSideSection={
                          <>
                            <img
                              src={getCloudImg('AWS')}
                              data-testid={`icon1-${1}`}
                              style={{ width: '24px', height: '24px' }}
                            />
                            <VerticalDivider height="24px"></VerticalDivider>
                            <StatusChipV3
                              bgColor={
                                buyer?.details?.activeContracts >= 1
                                  ? StatusChipBg.GREEN
                                  : StatusChipBg.RED
                              }
                              textColor={
                                buyer?.details?.activeContracts >= 1
                                  ? StatusChipText.GREEN
                                  : StatusChipText.RED
                              }
                              label={
                                buyer?.details?.activeContracts >= 1
                                  ? 'Active'
                                  : 'Churned'
                              }
                            />
                          </>
                        }
                        actionTooltip="View buyer details"
                      />
                      <BuyerDetailRow>
                        <>
                          <BuyerInfo
                            title={'TCV'}
                            value={
                              <LabraToolTipV2
                                tooltipPlacement="top"
                                label="Total contract value"
                              >
                                <div>
                                  <span className={classes.currency}>$</span>
                                  <span>
                                    {numberFormat(
                                      buyer?.details?.totalTcv,
                                      3
                                    ) || '-'}
                                  </span>
                                </div>
                              </LabraToolTipV2>
                            }
                          />
                          <BuyerInfo
                            title={getRenewalTitle(buyer?.details?.renewal)}
                            value={renderRenewalValue(
                              buyer?.details?.renewal,
                              buyer?.details?.autoRenew || false,
                              buyer?.details?.activeContracts
                            )}
                          />

                          <BuyerInfo
                            title={'Buyer since'}
                            value={
                              buyer?.details?.buyerSince
                                ? DateTime.fromISO(buyer.details.buyerSince, {
                                    zone: timeZone,
                                  }).toFormat(standardLabraDateFormat)
                                : '-'
                            }
                          />

                          <BuyerInfo
                            title={'Active contracts'}
                            value={`${buyer?.details?.activeContracts} out of ${buyer?.details?.totalContracts}`}
                            showDivider={false}
                          />
                        </>
                      </BuyerDetailRow>
                    </CardsContainer>
                  ))}
                </Grid>
                <Grid
                  className={classes.container}
                  size={{
                    xs: 12,
                    sm: 12,
                    md: isBelowMediumScreen ? 12 : 5,
                    lg: 5,
                  }}
                >
                  <OverviewSectionWrapper
                    heading="Upcoming renewals in next 2 months"
                    children={<UpcomingRenewals />}
                    icon={faTriangleExclamation}
                    infoTooltip={upcomingRenewalsOverviewInfoText}
                    actionLabel={'View all'}
                    handleAction={() =>
                      goToSubscriptions(subscriptionsRangeStatus.ACTIVE)
                    }
                  />
                </Grid>
              </Grid>
            </div>
          )}
        </>
      )}
    </>
  )
}
